import { TOKENS, TOKEN_CHOICES } from './tokens.js';
/**
 * Token **value** validation, by type — the security boundary of the branding
 * row, and the reason this package exists at all rather than being two token
 * tables that happen to agree.
 *
 * ## Why values are validated
 *
 * A token value is written into a CSS custom property. A value carrying `;` or
 * `}` could close the declaration and open another, which turns a branding row
 * into a styling injection — small, since the value never becomes script, but
 * not a thing to leave open on a surface where the *tenant* authors the row and
 * an operator publishes it. On the read side a value that fails keeps the
 * preset's, warns, and paints; on the write side it is a `400` with a sentence.
 *
 * ## The invariant, stated once
 *
 * **No accepted value, of any type, may close a declaration.** Everything below
 * is one way or another of enforcing that: `UNSAFE` for the types whose values
 * are ordinary CSS fragments, and an explicit shape for `url`, whose values are
 * references and whose one legal semicolon lives inside a data URI's prelude.
 */
/**
 * Characters that would let a value escape its declaration.
 *
 * Applied to every type **except `url`** — see `isValidTokenValue` for why the
 * order matters and what replaced it there.
 */
const UNSAFE = /[;{}<>\\]|\/\*|url\s*\(|expression\s*\(|@import/i;
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const COLOR_FN = /^(?:rgb|rgba|hsl|hsla|oklch|color-mix)\([^()]*\)$/i;
const LENGTH = /^-?(?:\d+\.?\d*|\.\d+)(?:px|rem|em|%|ch|vw|vh|pt)$/;
/** A font *stack*: names, quotes, commas, and the generic families. */
const FONT_STACK = /^[a-z0-9 '",._-]+$/i;
const PLAIN_TEXT = /^[a-z0-9 .,'"()#%/:_-]+$/i;
const BARE_NUMBER = /^\d+\.?\d*$/;
/**
 * The one inline shape a `url` token accepts: base64 image data, in four media
 * types, with **exactly one semicolon** and it is the `;base64` prelude.
 *
 * Narrower than the old `isSafeImageUrl`, which also admitted `jpg`, `gif` and
 * `avif`: that branch was unreachable for token values (see
 * `isValidTokenValue`), so nothing has ever been published through it, and the
 * moment a data URI becomes reachable is the moment to make its allowlist the
 * short one rather than the historical one. Adding a media type later is free;
 * it is the same "adding is free" rule the token table runs on.
 *
 * The payload is strict base64 — `A–Z a–z 0–9 + /` in quartets, with the one
 * legal padding tail. No whitespace, no `%`, no second `;`, no `{`, no `}`.
 */
const DATA_IMAGE = /^data:image\/(?:svg\+xml|png|jpeg|webp);base64,(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/i;
/**
 * What an `https:` reference may not contain.
 *
 * The value is interpolated into `url("…")` by the bundle, so a quote ends the
 * argument and a `;` or `}` ends the declaration. Parentheses and whitespace go
 * too: a URL that needs them can percent-encode them, and a URL that cannot is
 * not a URL anybody typed on purpose.
 */
const URL_UNSAFE = /[;{}()<>"'`\\\s]/;
/**
 * The ceiling on an ordinary token value. Generous for a colour, a length or a
 * font stack, and small enough that a row cannot become a stylesheet.
 */
export const MAX_TOKEN_VALUE_LENGTH = 400;
/**
 * The ceiling on a `url` token, which is the one type whose value can honestly
 * be long: an inline mascot is a *drawing*, not a link, and 400 characters of
 * base64 is about 300 bytes of SVG — enough to prove the branch works and not
 * enough to use it.
 *
 * It is not the binding limit and is not meant to be: `apps/admin-api` caps the
 * whole branding document at 32 KB, because the document rides inside
 * `tenant_config.config` and `guest-api` reads it in full on every booking page
 * load. This number only says "one token is not the whole budget".
 */
export const MAX_URL_TOKEN_LENGTH = 8_000;
/**
 * The one shape a development allowance may take: a plain-`http` origin on the
 * loopback names, with or without a port, and nothing after the authority.
 *
 * `localhost` and `127.0.0.1` only — not `*.localhost`, which is where the rig's
 * *tenant hostnames* live (`demo-lovespa.book.localhost`) and which would let
 * the allowance name a page origin rather than an asset origin. The mirror
 * that serves the pictures is one process on one port; that is what this names.
 */
const DEV_ORIGIN = /^http:\/\/(?:localhost|127\.0\.0\.1)(?::\d{1,5})?$/;
/**
 * `true` when `origin` is something `isSafeImageUrl` may be handed as an
 * allowance — exported so the one service that reads the variable can refuse a
 * bad value at boot with the same rule this function applies at each call,
 * rather than with a second regex that would one day admit a different set.
 */
export function isDevAssetOrigin(origin) {
    if (!DEV_ORIGIN.test(origin))
        return false;
    try {
        // `new URL(…).origin` is the canonical spelling; asking for equality means
        // an allowance is stored the way a URL's own origin will be compared to it.
        return new URL(origin).origin === origin;
    }
    catch {
        return false;
    }
}
/**
 * Images are references, not values: the token holds a bare URL and the
 * stylesheet wraps it in `url(…)`. Only `https:` and an inline base64 image
 * survive — an `http:` reference would be mixed content on a page the LB serves
 * over TLS, and anything else is a scheme with no business in a background.
 *
 * The one exception is `allowance` (`ImageUrlAllowance`): an `http:` reference
 * whose **origin equals** the allowance's, character for character. Equality on
 * the origin rather than a prefix test on the string, because `new URL` has
 * already normalised the host and port — `http://LOCALHOST:8086` and
 * `http://localhost:8086/` name the same origin and a string prefix would say
 * otherwise — and because a prefix of `http://localhost:80` would admit
 * `http://localhost:8000`. The character rule (`URL_UNSAFE`) runs first either
 * way: the allowance loosens the scheme, never what may be interpolated.
 */
export function isSafeImageUrl(value, allowance) {
    if (DATA_IMAGE.test(value))
        return true;
    if (URL_UNSAFE.test(value))
        return false;
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return false;
    }
    if (url.protocol === 'https:')
        return true;
    if (allowance === undefined || allowance === null)
        return false;
    // The allowance is measured again here rather than trusted: a caller that
    // built one from a string it did not check gets a refusal, not a hole.
    return (url.protocol === 'http:' &&
        isDevAssetOrigin(allowance.devOrigin) &&
        url.origin === allowance.devOrigin);
}
/**
 * `true` when `value` may be written into the custom property for `name`.
 *
 * ## The order is the fix
 *
 * This function used to run `UNSAFE` **before** dispatching on the token's
 * type. `UNSAFE` refuses any value containing `;`, and every data URI carries
 * one in `;base64,` — so `isSafeImageUrl`'s data branch was unreachable for
 * token values and had been since both were written, which is why
 * `image.mascot`'s "SVG-friendly" promise quietly meant "hosted SVG only".
 *
 * Type dispatch now comes first, and `url` is measured by `isSafeImageUrl`
 * alone. That is not a loosening of the boundary: the `url` shape is *stricter*
 * than `UNSAFE` — it is an allowlist of two forms rather than a denylist of
 * characters, and the invariant `UNSAFE` exists to protect (no accepted value
 * may close a declaration) holds for both of them by construction. The
 * semicolon a data URI is allowed is the one inside `;base64,`, and the base64
 * payload's character class contains no other.
 */
export function isValidTokenValue(name, value) {
    if (value.length === 0)
        return false;
    const { type } = TOKENS[name];
    if (type === 'url') {
        return value.length <= MAX_URL_TOKEN_LENGTH && isSafeImageUrl(value);
    }
    if (value.length > MAX_TOKEN_VALUE_LENGTH)
        return false;
    if (UNSAFE.test(value))
        return false;
    // The `text` tokens whose values are a vocabulary rather than a shape: the
    // bundle draws each by name, so a name it does not draw is refused at the
    // door rather than rendered as nothing.
    const choices = TOKEN_CHOICES[name];
    if (choices !== undefined)
        return choices.includes(value);
    switch (type) {
        case 'color':
            return HEX.test(value) || COLOR_FN.test(value);
        case 'length':
            return LENGTH.test(value) || value === '0';
        case 'number':
            return BARE_NUMBER.test(value);
        case 'font':
            return FONT_STACK.test(value);
        case 'text':
            return PLAIN_TEXT.test(value);
    }
}
/* ------------------------------------------------------------------ */
/* Outbound links (the `links` slot kind)                              */
/* ------------------------------------------------------------------ */
/**
 * **The ceiling on a link's label**, and it is short on purpose: a `links` row
 * is a line on a band, not a paragraph with an anchor in it. "Shop the full
 * line" fits; a sentence does not, and a sentence that did would wrap a button.
 */
export const MAX_LINK_LABEL_LENGTH = 60;
/** The ceiling on a link's destination. An ordinary URL, not a data payload. */
export const MAX_LINK_URL_LENGTH = 2_000;
/**
 * **How many rows a `links` slot may carry.** Six, which is the number at which
 * a row of links stops being a row and starts being a navigation bar the tenant
 * did not ask for.
 */
export const LINKS_MAX = 6;
/**
 * A tenant's **outbound** link — the one URL shape in this contract that
 * deliberately leaves the site.
 *
 * It exists because a real owner has an affiliate arrangement with the brand she
 * stocks (`docs/lovespa-discovery.md` §4: everyone off-island buys through *her*
 * link and she earns the credit), and the section system had nowhere structured
 * to put one. The alternatives were both worse: an `https://…` typed into a prose
 * slot, which renders as text nobody can click, or rung-5 custom HTML for a row
 * of two links.
 *
 * ## What is refused, and why each one
 *
 *  * **`https:` only.** `http:` is mixed content on a page the load balancer
 *    serves over TLS; `javascript:` and `data:` are script execution in a
 *    tenant-supplied string, which is the whole reason this is a function and
 *    not a `startsWith`.
 *  * **No quotes, angle brackets, backticks, whitespace or backslashes**
 *    (`URL_UNSAFE`, shared with `isSafeImageUrl`). The value is interpolated
 *    into an attribute, and a quote ends it. A URL that needs one of these can
 *    percent-encode it.
 *  * **No credentials in the authority** (`user:pass@`), which is a phishing
 *    shape rather than an address anybody types on purpose.
 *
 * What is **not** refused is a query string: an affiliate link *is* a query
 * string, and stripping one would break the only use this slot has. What the
 * platform must never do is *add* one — no `utm_`, no click id, nothing. The
 * renderer prints the URL the owner gave, exactly, with `rel="noopener
 * noreferrer"` and `target="_blank"`.
 */
export function isSafeLinkUrl(value) {
    if (value.length > MAX_LINK_URL_LENGTH)
        return false;
    if (URL_UNSAFE.test(value))
        return false;
    try {
        const url = new URL(value);
        if (url.protocol !== 'https:')
            return false;
        return url.username === '' && url.password === '';
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=values.js.map